<template>
	<div ref="hostEl" class="video-call-host">
		<!-- The one element that moves into a picture-in-picture window and back. It stays rendered (v-show) so Vue never has to insert or remove it while it is away. -->
		<div v-show="visible" ref="viewEl" class="video-call-view" :class="{ minimised, 'in-pip': inPip, 'is-fullscreen': isFullscreen }" @contextmenu.prevent>
			<template v-if="visible && call && state">
				<!-- MINIMISED -->
				<div v-if="minimised" class="video-chip">
					<GlyphIcon :icon="GlyphIconName['video']" size="16" />
					<div class="dotdotdot flex-1 min-w-0">{{ partyLabel }}</div>
					<Button size="small" variant="text" class="header-button" :aria-label="t('mycalls.videoShow')" :title="t('mycalls.videoShow')" @click="restore()">
						<i class="pi pi-window-maximize"></i>
					</Button>
				</div>

				<template v-else>
					<!-- HEADER -->
					<div class="dash-header">
						<GlyphIcon :icon="GlyphIconName['video']" color="#fff" size="18" style="margin-top:2px" />
						<div class="dotdotdot" style="margin-left:6px;margin-top:1px">{{ partyLabel }}</div>
						<div class="flex flex-row items-start gap-1.5">
							<Button v-if="!inPip" size="small" variant="text" class="header-button" :aria-label="t('mycalls.videoHide')" :title="t('mycalls.videoHide')" @click="minimise()">
								<i class="pi pi-window-minimize"></i>
							</Button>
						</div>
					</div>

					<div v-if="sharing" class="sharing-banner">{{ t('mycalls.videoScreenSharing') }}</div>

					<!-- VIDEO -->
					<div class="video-stage">
						<VideoTile class="remote-tile" :class="{ portrait: remotePortrait }" :stream="remoteStream" fit="contain" :placeholder="initials(party?.name)" @aspect="keepRemoteAspect">
							<template #placeholder>
								<EmployeePicture :picture="partyPicture" :name="party?.name" :size="72" />
								<div v-if="caption" class="stage-caption">{{ caption }}</div>
							</template>
						</VideoTile>

						<div v-if="caption && remoteStream" class="stage-overlay">
							<EmployeePicture :picture="partyPicture" :name="party?.name" :size="72" />
							<div class="stage-caption">{{ caption }}</div>
						</div>

						<div class="self-area">
							<div v-if="selfStream" class="self-view" :style="selfStyle" @pointerdown="dragStart" @pointermove="dragMove" @pointerup="dragEnd" @pointercancel="dragEnd">
								<!-- A shared screen whole and readable: not mirrored, not cropped -->
								<VideoTile :stream="selfStream" :mirrored="!sharing" :fit="sharing ? 'contain' : 'cover'" :placeholder="initials(employees.me?.name)" @aspect="value => selfAspect = value" />
							</div>
						</div>
					</div>

					<!-- CONTROLS - labelled with title, not v-tooltip: a tooltip opens in the main window, also while the view is in picture-in-picture -->
					<div class="video-controls">
						<Button v-if="showCamera && !sharing" size="small" rounded raised :severity="cameraOn ? 'secondary' : 'primary'" class="video-control" :disabled="cameraStarting || cameraUnavailable" :aria-label="cameraLabel" :title="cameraLabel" @click="toggleMyCamera()">
							<i v-if="cameraStarting" class="pi pi-spinner pi-spin"></i>
							<GlyphIcon v-else :icon="cameraOn ? GlyphIconName['video-off'] : GlyphIconName['video']" size="16" />
						</Button>
						<Select v-if="showCameraPicker && !sharing" class="camera-picker" size="small" :options="cameraOptions" optionLabel="label" optionValue="value" optionDisabled="disabled" :modelValue="currentCameraId" append-to="self" :aria-label="t('settings.phone.camera')" @update:modelValue="pickCamera" />
						<Button v-if="showShare" size="small" rounded raised :severity="sharing ? 'primary' : 'secondary'" class="video-control" :disabled="cameraStarting" :aria-label="shareLabel" :title="shareLabel" @click="toggleMyScreen()">
							<GlyphIcon :icon="GlyphIconName['computer-all-in-one']" size="16" />
						</Button>
						<Button size="small" rounded raised :severity="muted ? 'warn' : 'secondary'" class="video-control" :aria-label="muteLabel" :title="muteLabel" @click="toggleMute()">
							<GlyphIcon :icon="muted ? GlyphIconName['mic-off'] : GlyphIconName['mic']" size="16" />
						</Button>
						<Button size="small" rounded raised severity="danger" class="video-control" :aria-label="t('mycalls.action.hangup')" :title="t('mycalls.action.hangup')" @click="hangup()">
							<i class="pi pi-phone rotate-135"></i>
						</Button>
						<Button v-if="canFullscreen && !inPip" size="small" rounded raised severity="secondary" class="video-control" :aria-label="fullscreenLabel" :title="fullscreenLabel" @click="toggleFullscreen()">
							<GlyphIcon :icon="isFullscreen ? GlyphIconName['fullscreen-off'] : GlyphIconName['fullscreen']" size="16" />
						</Button>
						<Button v-if="canPip" size="small" rounded raised :severity="inPip ? 'primary' : 'secondary'" class="video-control" :aria-label="t('mycalls.videoPictureInPicture')" :title="t('mycalls.videoPictureInPicture')" @click="togglePip()">
							<i class="pi pi-clone"></i>
						</Button>
					</div>

					<!-- The app's screen-share source picker - here, so it is where the view is, in the pop-out window too -->
					<ScreenSourcePicker v-if="electron.displayMediaRequest" :request="electron.displayMediaRequest" @choose="id => electron.selectDisplayMediaSource(id)" @open-settings="electron.openScreenRecordingSettings()" />
				</template>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEmployeesStore } from '@/stores/Employees'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import { usePhonebookStore } from '@/stores/Phonebook'
import { useSettingsStore } from '@/stores/Settings'
import { useVideoCallsStore, type LocalCamera } from '@/stores/VideoCalls'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import { PbxCallState } from '@/TcxApi/PbxCommon'
import SipClient from '@/utilities/SipClient'
import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import { sleep } from '@/utilities/Tools'
import { canShareScreen, sessionFor, showCameraButton, sipCallIdFor, toggleCamera, toggleScreenShare } from '@/utilities/video/visibility'
import { useElectronStore } from '@/stores/Electron'
import GlyphIcon from '@/components/GlyphIcon.vue'
import EmployeePicture from '@/components/EmployeePicture.vue'
import VideoTile from '@/components/video/VideoTile.vue'
import ScreenSourcePicker from '@/components/video/ScreenSourcePicker.vue'

/** Chrome's Document Picture-in-Picture API - not in TypeScript's DOM types yet */
interface DocumentPictureInPicture {
	requestWindow(options?: { width?: number, height?: number }): Promise<Window>
}

const employees = useEmployeesStore()
const mediaDevices = useMediaDevicesStore()
const phonebook = usePhonebookStore()
const ss = useSettingsStore()
const videoCalls = useVideoCallsStore()
const { t } = useI18n()

const hostEl = ref<HTMLElement>()
const viewEl = ref<HTMLElement>()

// #region The call on screen

// Sessions live in SipClient, where Vue can't see them change: look them up again after every SIP event
const sipTick = ref(0)

function onSipClientEvent() {
	sipTick.value++
}

globalEvents.on('SipClient', onSipClientEvent)

/** The call the view was opened for - it stays on screen while that call is on hold */
const openFor = ref<string>()
const minimised = ref(false)

/** The call on screen: the answered one - else the held call the view was showing, so the user sees "On hold" rather than a frozen picture */
const call = computed(() => {
	const answered = employees.myCalls.find(aCall => aCall.state === PbxCallState.CALL_ANSWER)

	if (answered) { return answered }
	if (!openFor.value) { return undefined }

	void sipTick.value
	return employees.myCalls.find(aCall => aCall.state === PbxCallState.CALL_HOLD && sipCallIdFor(aCall) === openFor.value)
})

/** The call's softphone session */
const session = computed(() => {
	void sipTick.value
	return call.value ? sessionFor(call.value) : undefined
})

/** The key of the call's video state: the session's own Call-ID, not the realtime event's */
const sipCallId = computed(() => {
	void sipTick.value
	return call.value ? sipCallIdFor(call.value) : undefined
})

const state = computed(() => (sipCallId.value ? videoCalls.calls[sipCallId.value] : undefined))
const held = computed(() => call.value?.state === PbxCallState.CALL_HOLD)

/**
 * Whether my camera is on, or on its way
 * @param local My camera's state
 */
function cameraActive(local: LocalCamera | undefined) {
	return local === 'on' || local === 'starting'
}

/** Shown when the call has video - or had it while on screen and is on hold now */
const visible = computed(() => {
	const video = state.value

	if (!video) { return false }
	return openFor.value === video.callId || video.remote !== 'none' || cameraActive(video.local)
})

// Open by itself when the other party's video starts, or my camera comes on. A pause in the bridge ending is not a start.
watch(() => [ state.value?.callId, state.value?.remote, state.value?.local ] as const, ([ callId, remote, local ], previous) => {
	if (!callId) { return }

	const sameCall = previous?.[0] === callId
	const remoteStarted = remote === 'live' && (!sameCall || previous?.[1] === 'none')
	const cameraStarted = cameraActive(local) && (!sameCall || !cameraActive(previous?.[2]))

	if (remoteStarted || cameraStarted) {
		openFor.value = callId
		minimised.value = false
	}
}, { immediate: true })

function minimise() {
	minimised.value = true
}

function restore() {
	minimised.value = false
}

// #endregion

// #region The other party

const party = computed(() => {
	const aCall = call.value

	if (!aCall) { return undefined }
	if (aCall.leg === 'I') { return { name: aCall.callee.name ?? '', number: aCall.callee.number } }
	if (aCall.caller.secret) { return { name: t('mycalls.hiddenNumber'), number: '' } }
	return { name: aCall.caller.name ?? '', number: aCall.caller.number }
})

const partyLabel = computed(() => party.value?.name || ss.cutNationalPrefix(party.value?.number ?? '', true))

const partyPicture = computed(() => {
	const number = party.value?.number

	if (!number) { return undefined }
	return employees.findEmployeeFromNumber(number)?.picture ?? phonebook.findPictureFromNumber(number) ?? undefined
})

/**
 * A person's initials, for the placeholder circle
 * @param name The person's name
 */
function initials(name: string | undefined) {
	const parts = (name ?? '').trim().split(/\s+/).filter(part => part.length > 0)
	const first = parts[0]?.[0] ?? ''
	const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : ''

	return (first + last).toUpperCase()
}

// #endregion

// #region Video

/** The other party's video: none while their camera is off (their picture shows) or while on hold */
const remoteStream = computed(() => {
	const video = state.value

	return video && !held.value && video.remote !== 'none' ? video.remoteStream : undefined
})

/** What the stage says over the other party's picture: on hold, or the bridge has stopped the video (a spy, a recording ding) */
const caption = computed(() => {
	if (held.value) { return t('mycalls.videoOnHold') }
	if (state.value?.remote === 'paused') { return t('mycalls.videoPaused') }
	return undefined
})

const remotePortrait = computed(() => {
	const aspect = state.value?.remoteAspect

	return aspect !== undefined && aspect < 1
})

/**
 * Keep the other party's video shape - it changes when they turn their phone
 * @param aspect videoWidth / videoHeight
 */
function keepRemoteAspect(aspect: number) {
	const video = state.value

	if (video && video.remoteAspect !== aspect) { videoCalls.patch(video.callId, { remoteAspect: aspect }) }
}

const selfStream = computed(() => (state.value?.local === 'on' && !held.value ? state.value.localStream : undefined))
const selfAspect = ref(16 / 9)

/** Where my own camera sits in the stage: 0..1 across and down the free space - bottom right to start with */
const selfPosition = ref({ x: 1, y: 1 })

const selfStyle = computed(() => ({
	left: `${selfPosition.value.x * 100}%`,
	top: `${selfPosition.value.y * 100}%`,
	transform: `translate(${-selfPosition.value.x * 100}%, ${-selfPosition.value.y * 100}%)`,
	aspectRatio: String(selfAspect.value)
}))

let drag: { pointerId: number, startX: number, startY: number, from: { x: number, y: number }, freeWidth: number, freeHeight: number } | undefined

/**
 * Start dragging my own camera
 * @param event The pointerdown event on it
 */
function dragStart(event: PointerEvent) {
	const selfView = event.currentTarget as HTMLElement
	const area = selfView.parentElement

	if (!area) { return }

	const areaBox = area.getBoundingClientRect()
	const selfBox = selfView.getBoundingClientRect()

	drag = {
		pointerId: event.pointerId,
		startX: event.clientX,
		startY: event.clientY,
		from: { ...selfPosition.value },
		freeWidth: areaBox.width - selfBox.width,
		freeHeight: areaBox.height - selfBox.height
	}
	if (typeof selfView.setPointerCapture === 'function') { selfView.setPointerCapture(event.pointerId) }
	event.preventDefault()
}

/**
 * Follow the pointer, keeping my own camera inside the stage
 * @param event The pointermove event
 */
function dragMove(event: PointerEvent) {
	if (!drag || event.pointerId !== drag.pointerId) { return }

	const within = (value: number) => Math.min(1, Math.max(0, value))

	selfPosition.value = {
		x: drag.freeWidth > 0 ? within(drag.from.x + ((event.clientX - drag.startX) / drag.freeWidth)) : drag.from.x,
		y: drag.freeHeight > 0 ? within(drag.from.y + ((event.clientY - drag.startY) / drag.freeHeight)) : drag.from.y
	}
}

/**
 * Stop dragging
 * @param event The pointerup or pointercancel event
 */
function dragEnd(event: PointerEvent) {
	if (!drag || event.pointerId !== drag.pointerId) { return }

	const selfView = event.currentTarget as HTMLElement

	if (typeof selfView.releasePointerCapture === 'function') { selfView.releasePointerCapture(event.pointerId) }
	drag = undefined
}

// #endregion

// #region Camera

/**
 * The same rule as the call panel's camera button - hidden after "Benyt ikke", disabled once video was rejected, and on
 * hold there only while my camera is on, to turn it off: the stage still says "On hold"
 */
const showCamera = computed(() => {
	void sipTick.value
	return !!call.value && showCameraButton(call.value)
})
const cameraOn = computed(() => state.value?.local === 'on')
const cameraStarting = computed(() => state.value?.local === 'starting')
const cameraUnavailable = computed(() => !cameraOn.value && !!state.value?.unavailable)
const cameraLabel = computed(() => {
	if (cameraOn.value) { return t('mycalls.videoTurnOffCamera') }
	return cameraUnavailable.value ? t('mycalls.videoNotAvailable') : t('mycalls.videoTurnOnCamera')
})

function toggleMyCamera() {
	if (call.value) { toggleCamera(call.value, 'VideoCallView') }
}

// #endregion

// #region Screen sharing (screen-share spec §3)

const electron = useElectronStore()

/** My screen is shared on the call instead of my camera */
const sharing = computed(() => state.value?.source === 'screen')

/** "Share screen" on the camera button's terms where screen capture works - and "Stop sharing" while sharing */
const showShare = computed(() => {
	void sipTick.value
	return !!call.value && (sharing.value || canShareScreen(call.value))
})
const shareLabel = computed(() => (sharing.value ? t('mycalls.videoScreenShareStop') : t('mycalls.videoScreenShare')))

/** The cameras to pick from - 'Benyt ikke' is a preference, not a camera */
const cameraOptions = computed(() => mediaDevices.videoInDevices.filter(device => device.value !== 'NONE'))
const showCameraPicker = computed(() => cameraOn.value && cameraOptions.value.length > 1)

/** The camera the call is sending from, as its track reports it */
const currentCameraId = computed(() => state.value?.localStream?.getVideoTracks()[0]?.getSettings().deviceId)

/**
 * Move the call to another camera - the preferred camera in the settings stays as it is
 * @param deviceId The camera's deviceId
 */
function pickCamera(deviceId: string) {
	const callId = sipCallId.value

	if (!callId || !deviceId || deviceId === currentCameraId.value) { return }

	logger.info(`VideoCallView: User moves the call to camera ${deviceId}`)
	SipClient.instance.switchCamera(callId, deviceId).catch((err: unknown) => { logger.warning(`VideoCallView: Switching camera failed - ${(err as Error).message}`) })
}

// #endregion

// #region Microphone and hang up - the same session actions the headsets and the call panel use

const muted = computed(() => {
	void sipTick.value
	return session.value?.isMuted().audio ?? false
})

const muteLabel = computed(() => (muted.value ? t('mycalls.videoUnmuteMicrophone') : t('mycalls.videoMuteMicrophone')))

function toggleMute() {
	const aSession = session.value

	if (!aSession) { return }
	if (muted.value) {
		SipClient.instance.unmute(aSession)
	} else {
		SipClient.instance.mute(aSession)
	}
	sipTick.value++
}

function hangup() {
	const aSession = session.value

	if (!aSession) { return }

	logger.info('VideoCallView: User hangs up from the video view')
	globalEvents.emit('Call:Hangup', { sessionId: aSession.id })
}

// #endregion

// #region Full screen and picture-in-picture

const canFullscreen = document.fullscreenEnabled
const isFullscreen = ref(false)
const fullscreenLabel = computed(() => (isFullscreen.value ? t('mypresence.action.endFullScreen') : t('mypresence.action.startFullScreen')))

function onFullscreenChange() {
	isFullscreen.value = !!viewEl.value && document.fullscreenElement === viewEl.value
}

document.addEventListener('fullscreenchange', onFullscreenChange)

async function toggleFullscreen() {
	try {
		if (isFullscreen.value) {
			await document.exitFullscreen()
		} else {
			await viewEl.value?.requestFullscreen()
		}
	} catch (err) {
		logger.warning(`VideoCallView: Full screen failed - ${(err as Error).message}`)
	}
}

/** The API, where the browser has it (Chrome - Electron has it too, but can not use it, see inApp) */
function pictureInPicture(): DocumentPictureInPicture | undefined {
	return (window as Window & { documentPictureInPicture?: DocumentPictureInPicture }).documentPictureInPicture
}

/** The page the Communicator Desktop app's pop-out window loads: empty, and on CD4's own address, the only one the app opens in a window */
const POPOUT_PAGE = 'video-popout.html'

/** The pop-out window's smallest size: the controls keep to one row - five round buttons and the camera picker */
const POPOUT_MIN_WIDTH = 360
const POPOUT_MIN_HEIGHT = 300

/** How long the pop-out window may take to load its page */
const POPOUT_LOAD_TIMEOUT_MS = 5_000

/**
 * In the Communicator Desktop app (Electron), which has the picture-in-picture API but can not open its window - requestWindow
 * fails with "Internal error: no window" (electron/electron#39633). The view goes into an ordinary always-on-top window there.
 */
const inApp = ss.runtime.isEmbedded

const canPip = inApp || !!pictureInPicture()
const pipWindow = shallowRef<Window>()
const inPip = computed(() => !!pipWindow.value)

/** Share or stop sharing my screen - through the pop-out or picture-in-picture window when the view is there: Chrome wants
 * getDisplayMedia called in the window that was clicked */
function toggleMyScreen() {
	if (call.value) { toggleScreenShare(call.value, 'VideoCallView', pipWindow.value) }
}

/**
 * Give a picture-in-picture window the app's styles
 * @param target The window's document
 */
function copyStyles(target: Document) {
	for (const sheet of Array.from(document.styleSheets)) {
		try {
			const style = target.createElement('style')

			style.textContent = Array.from(sheet.cssRules, rule => rule.cssText).join('\n')
			target.head.append(style)
		} catch (_) {
			// A sheet from another origin (the theme, from the API) can't be read: link it instead
			if (sheet.href) {
				const link = target.createElement('link')

				link.rel = 'stylesheet'
				link.href = sheet.href
				target.head.append(link)
			}
		}
	}
}

let styleMirror: MutationObserver | undefined

/**
 * Keep giving a picture-in-picture window the styles added after it opened: PrimeVue adds a component's styles to the
 * main document the first time the component mounts - the camera picker may mount in the window
 * @param target The window's document
 */
function mirrorNewStyles(target: Document) {
	styleMirror = new MutationObserver(records => {
		for (const record of records) {
			for (const node of Array.from(record.addedNodes)) {
				if (node instanceof HTMLStyleElement || (node instanceof HTMLLinkElement && node.rel === 'stylesheet')) {
					target.head.append(target.importNode(node, true))
				}
			}
		}
	})
	styleMirror.observe(document.head, { childList: true })
}

/**
 * Carry on playing after a move between documents
 * @param element The view
 */
function resumeVideos(element: HTMLElement) {
	for (const video of Array.from(element.querySelectorAll('video'))) {
		video.play().catch(() => { /* a muted video may always play - a refusal just leaves the last frame */ })
	}
}

/**
 * Has the pop-out window got its own page yet
 * @param win The window
 */
function popoutLoaded(win: Window) {
	try {
		return win.location.pathname.endsWith(POPOUT_PAGE) && win.document.readyState === 'complete'
	} catch (_) {
		return false // Between documents
	}
}

/**
 * Open the Communicator Desktop app's pop-out window, and wait until it shows its own page - window.open hands the window
 * back while it still holds about:blank
 * @param width Width of the window
 * @param height Height of the window
 * @returns The window
 */
async function openPopout(width: number, height: number): Promise<Window> {
	const url = new URL(import.meta.env.BASE_URL + POPOUT_PAGE, location.origin).href
	const win = window.open(url, 'cd4-video', `popup,width=${Math.max(width, POPOUT_MIN_WIDTH)},height=${Math.max(height, POPOUT_MIN_HEIGHT)},minWidth=${POPOUT_MIN_WIDTH},minHeight=${POPOUT_MIN_HEIGHT},alwaysOnTop=yes`)

	if (!win) { throw new Error('the app did not open the window') }
	const started = Date.now()

	while (!popoutLoaded(win)) {
		if (win.closed || Date.now() - started > POPOUT_LOAD_TIMEOUT_MS) {
			win.close()
			throw new Error('the window did not load its page')
		}
		await sleep(50)
	}
	return win
}

/** Move the view into an always-on-top window, so it stays in sight while the user works elsewhere */
async function enterPip() {
	const api = pictureInPicture()
	const element = viewEl.value

	if ((!inApp && !api) || !element || pipWindow.value) { return }

	try {
		if (document.fullscreenElement) { await document.exitFullscreen() }

		const width = Math.max(element.clientWidth, 320)
		const height = Math.max(element.clientHeight, 240)
		const win = api && !inApp ? await api.requestWindow({ width, height }) : await openPopout(width, height)

		copyStyles(win.document)
		mirrorNewStyles(win.document)
		win.document.body.style.margin = '0'
		win.document.body.append(element)
		resumeVideos(element)
		// The window closing by any means - its own close button included - brings the view back
		win.addEventListener('pagehide', returnFromPip, { once: true })
		// A picture-in-picture window closes with the app's page, an ordinary window does not
		if (inApp) { window.addEventListener('pagehide', leavePip, { once: true }) }
		pipWindow.value = win
		logger.info('VideoCallView: Video moved to picture-in-picture')
	} catch (err) {
		logger.warning(`VideoCallView: Picture-in-picture failed - ${(err as Error).message}`)
	}
}

/** Put the view back in its place in the app */
function returnFromPip() {
	const element = viewEl.value

	styleMirror?.disconnect()
	styleMirror = undefined
	window.removeEventListener('pagehide', leavePip)
	if (element && hostEl.value && element.parentElement !== hostEl.value) {
		hostEl.value.append(element)
		resumeVideos(element)
	}
	pipWindow.value = undefined
}

function leavePip() {
	const win = pipWindow.value

	returnFromPip()
	win?.close()
}

async function togglePip() {
	if (pipWindow.value) {
		leavePip()
	} else {
		await enterPip()
	}
}

// Nothing to show, or shrunk to the chip: leave picture-in-picture and full screen
watch([ visible, minimised ], ([ isVisible, isMinimised ]) => {
	if (isVisible && !isMinimised) { return }
	if (pipWindow.value) { leavePip() }
	if (isFullscreen.value) {
		document.exitFullscreen().catch(() => { /* already left */ })
	}
})

// #endregion

onBeforeUnmount(() => {
	leavePip()
	globalEvents.off('SipClient', onSipClientEvent)
	document.removeEventListener('fullscreenchange', onFullscreenChange)
})
</script>

<style scoped>
	.video-call-host {
		display: contents;
	}

	.video-call-view {
		container: video-call / inline-size;
		position: fixed;
		right: 12px;
		bottom: 28px; /* above the status panel */
		width: min(36rem, calc(100vw - 24px));
		z-index: 90; /* over the dashboards, under the top bar and PrimeVue's overlays */
		display: flex;
		flex-direction: column;
		background: var(--bg-panel);
		border-radius: 5px;
		box-shadow: 0 6px 24px rgba(0, 0, 0, 0.35);
	}

	.video-call-view.minimised {
		width: auto;
		max-width: min(18rem, calc(100vw - 24px));
	}

	.video-chip {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 2px 3px 2px 8px;
		background-color: var(--bg-color-dashboard-header);
		color: var(--txt-color-dashboard-header);
		border-radius: 5px;
		font-size: var(--font-size-dashboard-header);
	}

	.video-stage {
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 9;
		max-height: calc(100vh - 12rem);
		overflow: hidden;
		background: #161616;
	}

	.remote-tile {
		position: absolute;
		inset: 0;
	}

	.stage-overlay {
		position: absolute;
		inset: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		background: rgba(0, 0, 0, 0.55);
	}

	.stage-caption {
		color: #eee;
		font-size: 0.9rem;
		letter-spacing: 0.3px;
		text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
	}

	/* The area my own camera can be dragged in - a margin from the stage's edges */
	.self-area {
		position: absolute;
		inset: 8px;
		pointer-events: none;
	}

	.self-view {
		position: absolute;
		width: 20%;
		min-width: 64px;
		border-radius: 4px;
		overflow: hidden;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
		pointer-events: auto;
		cursor: grab;
		touch-action: none;
	}

	.self-view:active {
		cursor: grabbing;
	}

	.video-controls {
		display: flex;
		flex-wrap: nowrap; /* one row - the camera picker gives way (min-width: 0) */
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 8px;
	}

	.video-control {
		width: 2.25rem;
		height: 2.25rem;
		padding: 0;
	}

	.camera-picker {
		min-width: 0;
		max-width: 12rem;
	}

	/* PrimeVue places the list by the main window, not the pop-out or picture-in-picture one the picker is in, so it could */
	/* open below - out of the window, which then scrolled everything up. The picker is at the bottom: open the list above */
	.video-call-view.in-pip .camera-picker :deep(.p-select-overlay) {
		top: auto !important;
		bottom: 100%;
		margin-top: 0 !important;
		margin-bottom: 2px;
	}

	.sharing-banner {
		padding: 0.25rem 0.75rem;
		background: var(--p-primary-color);
		color: var(--p-primary-contrast-color);
		font-size: 0.85rem;
		text-align: center;
	}

	/* Full screen, and the picture-in-picture window: the stage takes all the room there is, the video fitted in it */
	.video-call-view.is-fullscreen,
	.video-call-view.in-pip {
		position: relative; /* not fixed - and the source picker's layer is placed in it */
		inset: auto; /* the in-app offsets (right, bottom) would move it up and to the left, leaving a gap */
		width: 100vw;
		height: 100vh;
		border-radius: 0;
		box-shadow: none;
	}

	.video-call-view.is-fullscreen .video-stage,
	.video-call-view.in-pip .video-stage {
		flex: 1 1 auto;
		min-height: 0;
		aspect-ratio: auto;
		max-height: none;
	}

	.video-call-view.is-fullscreen .dash-header,
	.video-call-view.in-pip .dash-header {
		border-radius: 0;
	}
</style>
